// @flow
import type { ChildrenArray } from 'react';
import React from 'react';
import { css } from '../../emotion-import-helper.js';
import { usePrintMode } from '../../jsx-activity/usePrintMode.js';
import { productColor, productColorSunflower } from '../../designSystem.js';
import {
  INVOverview,
  INVActivitySection,
  INVReflection,
} from '../investigation/structural-components.js';

export const ActiveSectionContext = React.createContext<number>(0);

// This component generates navigation for a lesson by assigning an
// index to each section and using that as a flag to whether the section
// is active and visible.
export function InternalTabNavigationGenerator(props: {|
  children: ChildrenArray<
    | React$Element<typeof INVOverview>
    | React$Element<typeof INVActivitySection>
    | React$Element<typeof INVReflection>,
  >,
|}) {
  const [activeSection, setActiveSection] = React.useState(0);
  const childrenList = React.Children.toArray(props.children);
  // Note the validation for allowed children is done in the Investigation component
  const overviewElement = childrenList.find(
    (child) => child.type.name === INVOverview.name,
  );
  const reflectionElement = childrenList.find(
    (child) => child.type.name === INVReflection.name,
  );
  const activitySectionElements: Array<
    React$Element<typeof INVActivitySection>,
  > = (childrenList.filter(
    (child) => child.type.name === INVActivitySection.name,
  ): any);

  const processedSections = [
    ...(overviewElement !== undefined
      ? [{ element: overviewElement, title: 'Overview' }]
      : []),
    ...activitySectionElements.map((activitySection, idx) => ({
      element: activitySection,
      title:
        activitySectionElements.length > 1 ? `Activity ${idx + 1}` : 'Activity',
    })),
    ...(reflectionElement !== undefined
      ? [{ element: reflectionElement, title: 'Reflection' }]
      : []),
  ].map((section, idx) => ({
    element: React.cloneElement(section.element, {
      _sectionIndex: idx,
    }),
    title: section.title,
  }));

  const handleTabButtonClick = (clickedButton) => {
    setActiveSection(clickedButton.sectionIndex);
  };

  const inPrintMode = usePrintMode();
  return (
    <ActiveSectionContext.Provider value={activeSection}>
      {!inPrintMode && processedSections.length > 1 && (
        <InternalTabNavigation
          tabButtons={processedSections.map((section) => ({
            title: section.title,
            sectionIndex: section.element.props._sectionIndex,
          }))}
          onTabButtonClick={handleTabButtonClick}
        />
      )}
      {processedSections.map((section) => section.element)}
    </ActiveSectionContext.Provider>
  );
}

type TabButton = {| title: string, sectionIndex: number |};

function InternalTabNavigation({
  tabButtons,
  onTabButtonClick,
}: {|
  tabButtons: Array<TabButton>,
  onTabButtonClick: (clickedButton: TabButton) => void,
|}) {
  const activeSection = React.useContext(ActiveSectionContext);
  const BUTTON_WIDTH = 95;
  const BUTTON_MARGIN = 5;
  const LAYOUT_WIDTH = BUTTON_WIDTH + 2 * BUTTON_MARGIN;

  return (
    <div
      style={{
        overflowX: 'auto',
      }}
      className={css`
        ::-webkit-scrollbar {
          display: none;
        }
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
      `}
    >
      <div
        style={{
          display: 'flex',
          backgroundColor: productColorSunflower.seashell,
          borderRadius: 32,
          width: LAYOUT_WIDTH * tabButtons.length,
          margin: '12px 0',
          position: 'relative',
        }}
      >
        {tabButtons.map((button, index) => (
          <InternalActivityTabButton
            key={index}
            button={button}
            onTabButtonClick={onTabButtonClick}
            isActive={button.sectionIndex === activeSection}
            layoutWidth={LAYOUT_WIDTH}
          />
        ))}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            transition: `transform .3s ease`,
            backgroundColor: productColor.white,
            borderRadius: 32,
            boxShadow: '0px 1px 7px rgba(33, 55, 77, 0.12)',
            margin: BUTTON_MARGIN,
            width: BUTTON_WIDTH,
            transform: `translate3d(${activeSection * LAYOUT_WIDTH}px, 0, 0)`,
            transition: `transform .3s ease`,
          }}
        />
      </div>
    </div>
  );
}

function InternalActivityTabButton({
  button,
  onTabButtonClick,
  isActive,
  layoutWidth,
}) {
  const { isHovered, eventListeners } = useHover();
  const handleTabButtonClick = React.useCallback(() => {
    onTabButtonClick(button);
  }, [onTabButtonClick, button]);
  return (
    <div
      style={{
        userSelect: 'none',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexBasis: layoutWidth,
        height: 46,
        color: isActive ? productColor.eggplant100 : productColor.shuttleGrey,
        fontWeight: isActive || isHovered ? 600 : 400,
        transisiton: 'color .3s ease',
        zIndex: 1,
        cursor: 'pointer',
      }}
      {...eventListeners}
      onClick={handleTabButtonClick}
    >
      {button.title}
    </div>
  );
}

function useHover() {
  const [hover, setHover] = React.useState(false);
  const eventListeners = React.useMemo(
    () => ({
      onMouseEnter: () => setHover(true),
      onMouseLeave: () => setHover(false),
    }),
    [],
  );
  return {
    isHovered: hover,
    eventListeners,
  };
}
